import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { DataManager, WebApiAdaptor  } from '@syncfusion/ej2-data';
import {TextElement} from "./text-element.js";
import {HiddenElement} from "./hidden-element.js";

export class CountrySelectorElement extends TextElement {

    prefixField;
    countryField;
    countryHidden;
    fixedForm = false;
    noSubmit = true;

    popupSettings = {
        backdrop: null,
        scrollSetting: null,
        popup: null,
        ddl: null
    };

    popupMutationObserver = null;

    /**
     * Retrieves the value associated with the `countryField` key
     * from the `formData` object.
     *
     * @return {*} The value corresponding to the `countryField` in `formData`.
     */
    get dataValue() {
        return this.formData[this.countryField];
    }

    /**
     * Creates an instance of the class and initializes it with the given data.
     *
     * @param {Object} formData - The data used to populate the form.
     * @param {HTMLElement} parentElement - The parent HTML element where the form will be rendered.
     * @param {Array} collection - A collection of data related to the form or its fields.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Prepares and configures the rendering process for specific field elements.
     * Updates the configuration of field and hidden field elements.
     *
     * @return {void} This method does not return a value.
     */
    preRender() {
        this.countryField = this.field;
        this.field = `${this.countryField}_control`;

        this.countryHidden = Object.assign(new HiddenElement(this.formData, this.parentForm, this.collection), {field: this.countryField}).render();
    }

    /**
     * Builds and returns a DataManager instance configured with the specified country search parameter.
     *
     * @param {string} countrySearch - The search parameter used to filter countries in the DataManager URL.
     * @return {DataManager} The configured DataManager instance.
     */
    buildDataManager(countrySearch) {
        const url = `${this.rootForm.phpRoot}/Countries.php/${countrySearch ?? ""}`;
        return new DataManager({
            url: url,
            adaptor: new WebApiAdaptor ,
            crossDomain: true
        });
    }

    /**
     * Initializes and configures a country dropdown list with custom templates, filtering, and event handling.
     *
     * @return {void} No value is returned as this method sets up and renders the dropdown list component.
     */
    postRender() {
        let initialRender = true;

        let countryDropList  = new DropDownList({
            dataSource: this.buildDataManager(),
            fields: { text: 'name', value: 'key' },
            cssClass: this.cssClass,
            placeholder: 'Select your country',
            sortOrder: 'Ascending',
            popupHeight: '200px',
            itemTemplate: '<div class="country-dd-row"><div class="flag-img"><img src="${flag}" alt="${flagAlt}"/></div><div>+${callingCode}</div><div>${name}</div></div>',
            valueTemplate: '<div class="country-dd-row"><div class="flag-img"><img src="${flag}" alt="${flagAlt}"/></div><div>+${callingCode}</div><div>${name} </span></div></div>',
            allowFiltering: true,
            value: "US",
            change: args => this.setValue(args.itemData),
            open: args => this.onOpen(args, countryDropList),
            close: args => this.closeDialog(args, countryDropList),
            dataBound: args => {
                if (initialRender) {
                    const country = args.items.find(item => item.key == countryDropList.value);
                    initialRender = false;
                    this.setValue(country);
                }
            },
            filtering: args => {
                const searchText =((args.text ?? "").length < 2 && (args.text ?? "").length !== 0) ? null : args.text;
                args.updateData(this.buildDataManager(searchText));
            }
        });
        countryDropList.appendTo(this.domComponent);
        this.setValue(countryDropList.value);

    }

    /**
     * Handles the logic to execute when a dropdown popup is opened. It adjusts the popup's positioning,
     * prevents body scrolling, and adds additional behaviors required for proper functionality in a fixed form.
     *
     * @param {object} args - The arguments object containing information about the popup.
     * @param {object} args.popup - The popup object.
     * @param {HTMLElement} args.popup.element - The DOM element representing the popup.
     * @param {object} ddl - The dropdown list object containing details about the triggering element.
     * @return {void} This method does not return a value.
     */
    onOpen(args, ddl) {
        window.tiberforms = window.tiberforms ?? {};
        window.tiberforms.popupOpen = true;

        this.popupSettings.popup = args.popup.element;
        if (this.popupSettings.popup == null) {
            return;
        }

        this.buildDdBackdrop();

        this.popupSettings.scrollSetting = document.body.style.overflow;
        this.popupSettings.ddl = ddl;

        // document.body.style.overflow = 'hidden';

        this.popupSettings.popup.addEventListener('click', (e) => e.stopPropagation());
        this.observePopupMovement();
        args.event.stopPropagation();
    }

    /**
     * Observes the movement or position changes of the popup by monitoring `style` attribute changes.
     * Adjusts the popup's position dynamically to align with the target container.
     *
     * @return {void} This method does not return any value.
     */
    observePopupMovement() {
        this.popupMutationObserver = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                if (mutation.attributeName === "style") {
                        const rect = this.popupSettings.ddl.inputWrapper.container.getBoundingClientRect();
                        if (this.popupSettings.popup == null) {
                            return;
                        }

                        this.popupSettings.popup.style.left = `${rect.left}px`;
                        this.popupSettings.popup.style.top = `${rect.bottom + 3}px`;
                        this.popupSettings.popup.style.position = 'fixed';
                }
            });
        });
        this.popupMutationObserver.observe(this.popupSettings.popup, { attributes: true, attributeFilter: ["style"] });
    }

    /**
     * Closes the currently opened dialog and resets associated settings.
     *
     * @param {Object} args - The arguments passed when closing the dialog.
     * @param {Object} ddl - The dropdown list or additional data related to the dialog.
     * @return {void} This method does not return any value.
     */
    closeDialog(args, ddl) {
        this.removeDdBackdrop();
        document.body.style.overflow = this.popupSettings?.scrollSetting;

        this.popupSettings.popup = null;
        this.popupSettings.scrollSetting = null;
        if (window.tiberforms?.popupOpen != null) {
            window.tiberforms.popupOpen = false;
        }

    }

    /**
     * Removes the backdrop element associated with the popup settings if it exists.
     *
     * The method checks if the `backdrop` property in `popupSettings` is not null.
     * If a backdrop exists, it removes the backdrop element from the DOM and sets
     * the `backdrop` property to null.
     *
     * @return {void} This method does not return any value.
     */
    removeDdBackdrop() {
        if (this.popupSettings.backdrop != null) {
            this.popupSettings.backdrop.remove();

            this.popupSettings.backdrop = null;
        }
    }

    buildDdBackdrop() {
        this.removeDdBackdrop();

        this.popupSettings.backdrop = document.createElement('div');
        this.popupSettings.backdrop.style.position = 'fixed';
        this.popupSettings.backdrop.style.top = '0';
        this.popupSettings.backdrop.style.left = '0';
        this.popupSettings.backdrop.style.width = '100%';
        this.popupSettings.backdrop.style.height = '100%';
        this.popupSettings.backdrop.style.backgroundColor = 'rgba(0, 0, 0, 0)';
        this.popupSettings.backdrop.style.zIndex = '999';
        document.body.insertBefore(this.popupSettings.backdrop, this.popupSettings.popup);
    }

    setValue(itemData) {
        if (itemData === null) {
            return;
        }

        this.formData[this.prefixField] = (itemData?.callingCode?.toString() ?? "").length > 0 ? itemData.callingCode.toString() : null;
        if (this.formData[this.prefixField] === null) {
            delete this.formData[this.prefixField];
        }

        this.formData[this.countryField] = itemData.name;
        this.countryHidden.domComponent.value = itemData.name;
        this.formData[`${this.countryField}Active`] = itemData;

        this.emitChangeNotification();
        this.validateRequired();
    }
}
